import { useMemo, useRef, useState } from "react";
import Sidebar from "@/components/Sidebar";
import ResultsTable from "@/components/ResultsTable";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Toaster, toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Search, Upload, FileSpreadsheet, FileDown, Sparkles, X, Archive } from "lucide-react";

export default function Dashboard() {
  const [inputText, setInputText] = useState("");
  const [fileItems, setFileItems] = useState([]);
  const [fileName, setFileName] = useState("");
  const [tab, setTab] = useState("paste");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const fileInputRef = useRef(null);

  // Parse text: each line = "Nome, Categoria"
  const parsedItems = useMemo(() => {
    if (tab === "upload") return fileItems;
    const items = [];
    const seen = new Set();
    for (const line of inputText.split(/\r?\n/)) {
      const t = line.trim();
      if (!t) continue;
      const parts = t.split(/\t|,|;/).map((p) => p.trim());
      const name = parts[0] || "";
      const category = parts.slice(1).join(" ").trim();
      if (!name) continue;
      const key = `${name.toLowerCase()}|${category.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ name, category });
    }
    return items;
  }, [inputText, fileItems, tab]);

  const stats = useMemo(() => {
    const found = results.filter((r) => r.status === "found").length;
    return { total: results.length, found, missing: results.length - found };
  }, [results]);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/parse-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFileItems(data.items);
      toast.success(`${data.count} itens carregados do arquivo`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao ler arquivo");
      setFileName("");
      setFileItems([]);
    }
  };

  const clearFile = () => {
    setFileItems([]);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const runSearch = async () => {
    if (parsedItems.length === 0) {
      toast.error("Informe pelo menos um nome de produto válido");
      return;
    }
    setLoading(true);
    setResults([]);
    setProgress(5);

    const startedAt = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const p = Math.min(85, 5 + elapsed * 3);
      setProgress(p);
    }, 400);

    try {
      const { data } = await api.post("/search", { items: parsedItems });
      setResults(data);
      setProgress(100);
      const found = data.filter((r) => r.status === "found").length;
      toast.success(`Busca concluída: ${found}/${data.length} produtos com imagens`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na busca");
    } finally {
      clearInterval(interval);
      setLoading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const downloadFile = async (fmt) => {
    if (results.length === 0) return;
    const isZip = fmt === "zip";
    if (isZip) toast.info("Empacotando imagens em ZIP... pode levar alguns segundos");
    try {
      const token = localStorage.getItem("ean_token");
      const res = await fetch(`${API}/export/${fmt}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ results }),
      });
      if (!res.ok) throw new Error("Falha no download");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
      const ext = fmt === "xlsx" ? "xlsx" : fmt === "zip" ? "zip" : "csv";
      a.download = `ean_imagens_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Arquivo ${fmt.toUpperCase()} baixado`);
    } catch (e) {
      toast.error("Erro ao gerar arquivo");
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 p-6 sm:p-10">
        <Toaster position="top-right" richColors />

        {/* Header */}
        <header className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="eyebrow mb-2">Nova Busca</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B]">
              Encontrar imagens por Nome + Categoria
            </h1>
            <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
              Informe <span className="font-mono text-zinc-800">Nome do produto, Categoria</span> em cada linha,
              ou envie planilha com <strong>Coluna A = Nome</strong> e <strong>Coluna B = Categoria</strong>
              (ex: <em>medicamento, alimento, cosmético</em>). A categoria refina a busca e aumenta a assertividade.
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm font-mono">
            <div>
              <div className="eyebrow">Itens</div>
              <div className="text-2xl text-[#09090B]" data-testid="ean-count">{parsedItems.length}</div>
            </div>
            <div className="w-px h-10 bg-zinc-200" />
            <div>
              <div className="eyebrow">Encontrados</div>
              <div className="text-2xl text-[#16A34A]" data-testid="found-count">{stats.found}</div>
            </div>
          </div>
        </header>

        {/* Input Card */}
        <section className="border border-zinc-200 bg-white rounded-md p-6 mb-6">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-zinc-100 rounded-md p-1 mb-4">
              <TabsTrigger
                value="paste"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-sm px-4 py-1.5 text-sm"
                data-testid="tab-paste"
              >
                Colar Códigos
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-sm px-4 py-1.5 text-sm"
                data-testid="tab-upload"
              >
                Upload de Arquivo
              </TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="mt-0">
              <Textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={`Formato: Nome do produto, Categoria (uma por linha)\n\nExemplos:\nLeite Moça Nestlé 395g, alimento\nDipirona sódica 500mg, medicamento\nShampoo Pantene Hidratação, cosmético\nNotebook Dell Inspiron 15, eletrônico\nRação Golden para cães, pet\nGeladeira Brastemp Frost Free, eletrodoméstico`}
                className="min-h-[220px] font-mono text-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="ean-textarea"
              />
              <div className="text-xs text-zinc-500 mt-2 font-mono">
                Separadores aceitos: vírgula, ponto-e-vírgula ou tabulação. Categoria é opcional mas melhora muito a assertividade. Máx. 200 itens.
              </div>
            </TabsContent>

            <TabsContent value="upload" className="mt-0">
              <div className="border-2 border-dashed border-zinc-300 rounded-md p-8 text-center bg-zinc-50/50">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.txt"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="file-input"
                />
                {!fileName ? (
                  <div className="space-y-3">
                    <div className="w-12 h-12 rounded-md bg-white border border-zinc-200 mx-auto flex items-center justify-center">
                      <Upload className="w-5 h-5 text-zinc-500" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-700 font-medium">Enviar planilha CSV ou XLSX</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        <strong>Coluna A</strong> = Nome do produto &nbsp;·&nbsp; <strong>Coluna B</strong> = Categoria
                      </p>
                    </div>
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="rounded-md border-zinc-300 hover:bg-white"
                      data-testid="upload-button"
                    >
                      Selecionar arquivo
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-md p-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileSpreadsheet className="w-5 h-5 text-[#002FA7] flex-shrink-0" />
                      <div className="text-left min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">{fileName}</div>
                        <div className="text-xs text-zinc-500 font-mono">{fileItems.length} itens carregados</div>
                      </div>
                    </div>
                    <button
                      onClick={clearFile}
                      className="text-zinc-400 hover:text-zinc-700"
                      data-testid="clear-file-button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Action */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 pt-6 border-t border-zinc-200">
            <div className="text-xs font-mono text-zinc-500">
              {parsedItems.length > 0
                ? `${parsedItems.length} item(ns) pronto(s) para busca`
                : "Aguardando dados..."}
            </div>
            <Button
              onClick={runSearch}
              disabled={loading || parsedItems.length === 0}
              className="h-11 px-6 rounded-md brand-primary text-white font-medium transition-colors disabled:opacity-50"
              data-testid="search-button"
            >
              <Search className="w-4 h-4 mr-2" />
              {loading ? "Buscando imagens..." : "Buscar Imagens"}
            </Button>
          </div>

          {progress > 0 && (
            <div className="mt-4">
              <Progress value={progress} className="h-1.5 bg-zinc-100" data-testid="progress-bar" />
              <div className="text-xs text-zinc-500 mt-2 font-mono">
                Processando {parsedItems.length} itens...
              </div>
            </div>
          )}
        </section>

        {/* Results */}
        {results.length > 0 && (
          <section>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <div className="eyebrow mb-1 flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-[#002FA7]" />
                  Resultado
                </div>
                <h2 className="font-display text-2xl font-semibold text-[#09090B]">
                  {stats.total} produtos processados · {stats.found} com imagens
                </h2>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => downloadFile("csv")}
                  variant="outline"
                  className="rounded-md border-zinc-300 hover:bg-zinc-50"
                  data-testid="export-csv-button"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Baixar CSV
                </Button>
                <Button
                  onClick={() => downloadFile("xlsx")}
                  variant="outline"
                  className="rounded-md border-zinc-300 hover:bg-zinc-50"
                  data-testid="export-xlsx-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Baixar Excel
                </Button>
                <Button
                  onClick={() => downloadFile("zip")}
                  className="rounded-md brand-primary text-white transition-colors"
                  data-testid="export-zip-button"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Baixar ZIP (imagens)
                </Button>
              </div>
            </div>
            <ResultsTable results={results} />
          </section>
        )}

        {results.length === 0 && !loading && (
          <div className="border border-dashed border-zinc-200 rounded-md p-12 text-center bg-white/50">
            <div className="eyebrow mb-3">Sem resultados ainda</div>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">
              Informe nomes de produtos e categorias acima e clique em{" "}
              <span className="font-mono text-zinc-800">Buscar Imagens</span> para gerar
              a planilha com URLs de imagens, descrição e indicação da fonte.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
